import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';
import { ReportstateProps } from './reportSlice';
import {
  capitalizeFirstLetter,
  convertDatetoEpoch,
  convertEpochToDateandTime,
  convertEpochToDateString,
  convertEpochToDateTime,
  convertEpochToFormattedDate,
  convertEpochToStartDateString
} from '../../../../../utils/commonFunctions';

import data from '@iconify-icons/mdi/minus';

type ReturnedType = any;

interface AddTripDetails {
  tripID?: string;
  tripType?: string;
  driverId: string;
  startGeoZoneColor: string;
  startGeoZoneRadius: number | null;
  startTimestamp: number;
  endGeoZoneColor: string;
  endGeoZoneRadius: number | null;
  endTimestamp: number;
  destination: string;
  endLat: number;
  endLng: number;
  source: string;
  startLat: number;
  startLng: number;
  vehicleNumber: string;
  waypointDetails?: Array<{
    waypointAddress: string;
    waypointLat: number;
    waypointLng: number;
    waypointColor?: string;
    waypointRadius?: number;
  }>;
}

interface WaypointDetails {
  waypointAddress: string;
  waypointLat: number;
  waypointLng: number;
  waypointColor: string;
}

export interface PreviewTrip {
  vehicleNumber: string;
  source: string;
  startLat: number;
  startLng: number;
  destination: string;
  endLat: number;
  endLng: number;
  waypointDetails?: WaypointDetails[] | [];
}

type TripDeactivateData = {
  endTimestamp: number;
  startTimestamp: number;
  tripID: number;
  tripcode: string;
  vehicleNumber: string;
  tripType: string;
  setDeactivate: any;
};

interface WaypointDetail {
  waypointColor: string;
  waypointRadius: number;
  waypointAddress: string;
  waypointLat: number;
  waypointLng: number;
  estimatedArrivalTime: string | null;
  estimatedDistance: number | null;
}

interface RouteResponse {
  totalDistanceInMeters: number;
  totalDurationInSeconds: number;
  encodedPolyLine: string;
  waypointDetails: WaypointDetail[];
}

interface ShareTrip {
  tripCode: string;
  tripId: number;
  url?: string;
  email: string;
  contactNumber: string;
  source: string;
  destination: string;
  startTime: string;
  endTime: string;
  vehicleNumber: string;
  driverName: string;
}

const initialState = {
  isLoading: false,
  data: [],
  message: '',
  availableDrivers: [],
  error: null
};

interface PreviewTripState {
  isLoading: boolean;
  data: RouteResponse | null;
  error: string | null;
}

const previewTripInitialState: PreviewTripState = {
  isLoading: false,
  data: null,
  error: null
};

interface ActiveDrivers {
  driverId: string;
  driverName: string;
}

interface DriverResponse {
  status: string;
  message: string;
  data: ActiveDrivers[] | [];
}

interface DriverInitialState {
  isLoading: boolean;
  data: ActiveDrivers[] | [];
  error: string | null;
}

const driverInitialState: DriverInitialState = {
  isLoading: false,
  data: [],
  error: null
};

interface TripPayload {
  tripType: string;
  pageNo: number;
  pageSize: number;
  driverID?: string;
  signal?: any;
}

interface DriverId {
  driverId: string;
}

const driverIdInitialState: DriverId = {
  driverId: ''
};

// trip details
export const tripsAction = createAsyncThunk<AxiosResponse<any>>(
  'user/trips',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.TRIP_DETAILS}`
      });
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// drivers list drivers availability
// action
const driverTripsState = {
  isLoading: false,
  data: null,
  error: null
};
export const getDriverslist = createAsyncThunk<
  AxiosResponse<any>,
  { tripStart: number; tripEnd: number; vehicleNumber: string; tripId: number }
>('drivers/list', async (data, thunkApi) => {
  const { tripStart, tripEnd, vehicleNumber, tripId } = data;
  try {
    let url = `${URLs.DRIVERS_LIST}?tripStart=${tripStart}&tripEnd=${tripEnd}&vehicleNumber=${vehicleNumber}`;
    if (tripId) url += `&tripId=${tripId}`;
    const response = await axiosInstance({
      method: 'GET',
      url: url
    });

    return response?.data;
  } catch (error: any) {
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: error?.response?.data?.message,
        severity: 'error'
      })
    );
    return thunkApi.rejectWithValue(error);
  }
});

// reducer
const driverTripSlice = createSlice({
  name: 'drivers',
  initialState: driverTripsState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getDriverslist.pending, state => {
        state.isLoading = true;
        state.data = null;
      })
      .addCase(getDriverslist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload?.data;
      })

      .addCase(getDriverslist.rejected, (state, action) => {
        state.isLoading = false;
        state.data = null;
        // state.error = action.payload;
      });
  }
});

// device list
export const tripsDeviceAction = createAsyncThunk<AxiosResponse<any>>(
  'user/trips/device',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.TRIP_DEVICE_DETAILS}`
      });
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// mapped device list
export const mappedTripsDeviceAction = createAsyncThunk<AxiosResponse<any>>(
  'user/mapped/trips/device',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.MAPPED_TRIP_DEVICE_DETAILS}`
      });
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const previewTripAction = createAsyncThunk<RouteResponse, PreviewTrip>(
  'preview/trip',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.PREVIEW_TRIP}`,
        data
      });
      // await thunkApi.dispatch(tripsAction());
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// add trips-driver

export const addTripsAction = createAsyncThunk<ReturnedType, AddTripDetails>(
  'add/trips',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.ADD_TRIP}`,
        data
      });

      await thunkApi.dispatch(
        tripTypesAction({ tripType: 'Scheduled', pageNo: 1, pageSize: 5 })
      );

      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );

      return response?.data?.data[0];
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to update trip

// Update Trips Action
export const updateTripsAction = createAsyncThunk<ReturnedType, AddTripDetails>(
  'update/trips',
  async (data, thunkApi) => {
    const { tripID, ...rest } = data;

    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.UPDATE_TRIP}`,
        data: { tripID, ...rest }
      });

      // Dispatch additional action if needed
      await thunkApi.dispatch(
        tripTypesAction({
          tripType: 'Scheduled',
          pageNo: 1,
          pageSize: 5
        })
      );

      // Show success message
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );

      return response?.data?.data[0];
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );

      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to activate and deactivate trip
export const deactivateAction = createAsyncThunk<ReturnedType, TripDeactivateData>(
  'trip/deactivate',
  async (data, thunkApi) => {
    const { tripType, setDeactivate, ...rest } = data;
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.DEACTIVATE_TRIP}`,
        data: rest
      });

      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data[0];
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// trip details
export const liveStatusAction = createAsyncThunk<AxiosResponse<any>>(
  'user/trips/livestatus',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.LIVE_STATUS}`
      });
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const driverAction = createAsyncThunk<DriverResponse>(
  'user/drivers',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.ACTIVE_DRIVERS}`
      });
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// share trip
export const shareTripsAction = createAsyncThunk<ReturnedType, ShareTrip>(
  'share/trips',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.SHARE_TRIP}`,
        data
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'Trip Shared successfully',
          severity: 'success'
        })
      );
      return response.data.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);
// my trips details slice
const mytripsSlice = createSlice({
  name: 'trips',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(tripsAction.pending, state => {
        state.isLoading = true;
        state.data = [];
        state.error = null;
      })
      .addCase(tripsAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload.data.map((item: any, index: number) => ({
          ...item,
          id: item?.tripID
        }));
        state.error = null;
      })
      .addCase(tripsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// my trips device list slice
const mytripsDeviceSlice = createSlice({
  name: 'trips devices',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(tripsDeviceAction.pending, state => {
        state.isLoading = true;
        state.data = [];
        state.error = null;
      })
      .addCase(tripsDeviceAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.error = null;
      })
      .addCase(tripsDeviceAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// mapped my trips device list slice
const mappedmytripsDeviceSlice = createSlice({
  name: 'trips devices',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(mappedTripsDeviceAction.pending, state => {
        state.isLoading = true;
        state.data = [];
        state.error = null;
      })
      .addCase(mappedTripsDeviceAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.error = null;
      })
      .addCase(mappedTripsDeviceAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// Add my trips slice
const addTrip = createSlice({
  name: 'add trips',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(addTripsAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addTripsAction.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;

        // Include driverId in the payload
        if (action.payload?.driverId) {
          action.payload.driverId = action.payload.driverId; // Ensure driverId is included
        }

        // Format timestamps if present in the payload
        if (action.payload?.startTimestamp) {
          action.payload.startTimestamp = convertEpochToDateString(
            action.payload.startTimestamp
          );
        }
        if (action.payload?.endTimestamp) {
          action.payload.endTimestamp = convertEpochToDateString(
            action.payload.endTimestamp
          );
        }

        state.data = action.payload; // Assuming payload contains the trip data directly
        state.error = null;
      })
      .addCase(addTripsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error || 'Something went wrong';
      });
  }
});

const previewTrip = createSlice({
  name: 'preview trip',
  initialState: previewTripInitialState,
  reducers: {
    clearPreview: state => {
      state.data = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(previewTripAction.pending, state => {
        state.isLoading = true;
        state.data = null;
        state.error = null;
      })
      .addCase(
        previewTripAction.fulfilled,
        (state, action: PayloadAction<RouteResponse>) => {
          state.isLoading = false;
          state.data = action.payload;
          state.error = null;
        }
      )
      .addCase(previewTripAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = null;
        state.error = action.payload?.response?.data?.error || 'Something went wrong';
      });
  }
});

// Update my trips slice
const updateTrip = createSlice({
  name: 'trips update',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(updateTripsAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateTripsAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;

          // Include driverId in the payload
          if (action.payload?.driverId) {
            action.payload.driverId = action.payload.driverId; // Ensure driverId is included
          }

          // Format timestamps if present in the payload
          if (action.payload?.startTimestamp) {
            action.payload.startTimestamp = convertEpochToDateString(
              action.payload.startTimestamp
            );
          }
          if (action.payload?.endTimestamp) {
            action.payload.endTimestamp = convertEpochToDateString(
              action.payload.endTimestamp
            );
          }

          state.data = action.payload; // Assuming payload contains the updated trip data directly
          state.error = null;
        }
      )
      .addCase(updateTripsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error || 'Something went wrong';
      });
  }
});

// Deavtivate my trips slice
const deactivateSlice = createSlice({
  name: 'trips deactivate',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(deactivateAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        deactivateAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload?.data;
          state.error = null;
        }
      )
      .addCase(deactivateAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response.data?.error || 'Something went wrong';
      });
  }
});

// my trips live ststus slice
const liveStatusSlice = createSlice({
  name: 'trips live status',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(liveStatusAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(liveStatusAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload.data;
      })
      .addCase(liveStatusAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response.data?.error;
      });
  }
});

const driverSlice = createSlice({
  name: 'drivers/active',
  initialState: driverInitialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(driverAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(driverAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action?.payload?.data;
        state.error = null;
      })
      .addCase(driverAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action?.payload?.response?.data?.error;
      });
  }
});

// share trips slice
const shareTrip = createSlice({
  name: 'share trips',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(shareTripsAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        shareTripsAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload?.data;
          state.error = null;
        }
      )
      .addCase(shareTripsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response.data?.error || 'Something went wrong';
      });
  }
});

const tripsState: ReportstateProps = {
  isLoading: false,
  error: null,
  rows: [],
  isActive: [],
  columns: [],
  count: null
};
//action for trips details
export const tripTypesAction = createAsyncThunk<ReturnedType, TripPayload>(
  'expiredtrips',
  async (params, thunkApi) => {
    const { tripType, pageNo, pageSize, driverID, signal } = params;
    try {
      let URL = `${URLs.TRIP_DETAILS}?tripType=${tripType}&pageno=${pageNo}&pagesize=${pageSize}`;
      if (driverID) {
        URL += `&driverId=${driverID}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: URL,
        signal: signal
      });
      return response?.data?.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message || 'Something went wrong',
            severity: 'error'
          })
        );
      }
      return thunkApi.rejectWithValue(error?.response?.data || 'Error');
    }
  }
);

const tripStatus: any = {
  PENDING: 'Pending',
  IN_TRANSIT: 'In-transit'
};

// tripsTypes slice
const tripTypesSlice = createSlice({
  name: 'tripTypes',
  initialState: tripsState,
  reducers: {
    previousState: (state, action: any) => {
      state.isActive = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(tripTypesAction.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.error = null;
      })
      .addCase(tripTypesAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.rows = action?.payload?.tripDetailsList?.map((item: any) => ({
          ...item,
          id: item?.tripID,
          startTime: convertEpochToStartDateString(item.startTime),
          endTime: convertEpochToStartDateString(item.endTime),
          actualStartTimestamp: convertEpochToStartDateString(item.actualStartTime),
          actualEndTimestamp: convertEpochToStartDateString(item.actualEndTime),
          startTimestamp: item.startTime,
          endTimestamp: item.endTime,
          driverName: item?.driverName ? capitalizeFirstLetter(item?.driverName) : 'NA',
          temporaryDriverName: item?.temporaryDriverName
            ? capitalizeFirstLetter(item?.temporaryDriverName)
            : 'NA',
          tripCode: item?.code,
          status: tripStatus[item?.tripStatus]
        }));

        state.count = action.payload?.tripListCount;
        state.error = null;
      })
      .addCase(tripTypesAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.rows = [];
        state.error = action.payload?.response?.data?.error;
      });
  }
});

//action for scheduled trips details
export const scheduledTripsAction = createAsyncThunk<ReturnedType, TripPayload>(
  'scheduledtrips',
  async (params, thunkApi) => {
    try {
      const { tripType, pageNo, pageSize } = params;
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.TRIP_DETAILS}?tripType=${tripType}&pageno=${pageNo}&pagesize=${pageSize}`
      });
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
    }
  }
);

//scheduled trips slice
const scheduledTripSlice = createSlice({
  name: 'scheduledtrips',
  initialState: tripsState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(scheduledTripsAction.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.error = null;
      })

      .addCase(scheduledTripsAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.rows = action?.payload?.tripDetailsList?.map(
          (item: any, index: number) => ({
            ...item,
            id: item?.tripID,
            startTime: convertEpochToDateString(item.startTime),
            endTime: convertEpochToDateString(item.endTime),
            startTimestamp: item.startTime,
            endTimestamp: item.endTime,
            tripType: 'Scheduled',
            tripCode: item?.code
          })
        );
        state.count = action.payload?.tripListCount;
        state.error = null;
      })
      .addCase(scheduledTripsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.rows = [];
        state.error = action.payload?.response?.data?.error;
      });
  }
});

//action for completed trips details
export const completedTripsAction = createAsyncThunk<ReturnedType, TripPayload>(
  'completedtrips',
  async (params, thunkApi) => {
    try {
      const { tripType, pageNo, pageSize } = params;
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.TRIP_DETAILS}?tripType=${tripType}&pageno=${pageNo}&pagesize=${pageSize}`
      });
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
    }
  }
);

//slice for completed trips
const completedTripsSlice = createSlice({
  name: 'completedtrips',
  initialState: tripsState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(completedTripsAction.pending, state => {
        state.isLoading = true;
        state.rows = [];
        state.error = null;
      })

      .addCase(completedTripsAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.rows = action?.payload?.tripDetailsList?.map(
          (item: any, index: number) => ({
            ...item,
            id: item?.tripID,
            startTime: convertEpochToDateString(item.startTime),
            endTime: convertEpochToDateString(item.endTime),
            startTimestamp: item.startTime,
            endTimestamp: item.endTime,
            tripType: 'Completed'
          })
        );
        state.count = action.payload.tripListCount;
        state.error = null;
      })
      .addCase(completedTripsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.rows = [];
        state.error = action.payload?.response?.data?.error;
      });
  }
});

//action tp delete trips
export const deleteTrips = createAsyncThunk<any, any>(
  'deletetrips',
  async (data, thunkApi) => {
    const { tripType, tripID } = data;
    try {
      const response = await axiosInstance({
        method: 'DELETE',
        url: `${URLs.DELETE_TRIPS}?tripId=${tripID}`
      });

      if (response) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: 'Trip Deleted successfully',
            severity: 'success'
          })
        );
      }

      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
    }
  }
);

//reducer for delete trips
const deleteTripsSlice = createSlice({
  name: 'deletetrips',
  initialState,
  reducers: {},
  extraReducers: builder =>
    builder
      .addCase(deleteTrips.pending, state => {
        state.isLoading = true;
        state.data = [];
      })
      .addCase(deleteTrips.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(deleteTrips.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
        state.data = [];
      })
});

// for storing driver id from driver module
const driverIdsReducer = createSlice({
  name: 'driverId',
  initialState: driverIdInitialState,
  reducers: {
    driverIds: (state, action: PayloadAction<any>) => {
      state.driverId = action.payload;
    }
  }
});

export default mytripsSlice.reducer;
export const tripDeviceSlice = mytripsDeviceSlice.reducer;
export const mappedtripDeviceSlice = mappedmytripsDeviceSlice.reducer;
export const previewTripSlice = previewTrip.reducer;
export const { clearPreview } = previewTrip.actions;
export const addTripSlice = addTrip.reducer;
export const updateTripSlice = updateTrip.reducer;
export const deactivateTripSlice = deactivateSlice.reducer;
export const liveStatusTripSlice = liveStatusSlice.reducer;
export const activeDriversReducer = driverSlice.reducer;
export const shareTripSlice = shareTrip.reducer;
export const tripTypesReducer = tripTypesSlice.reducer;
export const scheduledTripReducer = scheduledTripSlice.reducer;
export const completedTripReducer = completedTripsSlice.reducer;
export const deleteTripsData = deleteTripsSlice.reducer;
export const drivertripsdata = driverTripSlice.reducer;
export const { previousState } = tripTypesSlice.actions;
export const { driverIds } = driverIdsReducer.actions;
export const driverIdsReducers = driverIdsReducer.reducer;
